import { REVIEW_SOURCE_DEFINITIONS, type ReviewSourceId, type WidgetType } from '@flexbyt/shared';
import { SourceBadge, SourceGlyph, cx } from '@flexbyt/widget-preview';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Stars } from '../ui/review.js';
import { LayoutSketch } from '../widgets/LayoutSketch.js';
import { Jet, type JetMood } from './Jet.js';

/**
 * The right-hand side of a page hero: Jet, and a few pieces of the product
 * floating around it.
 *
 * ---------------------------------------------------------------------------
 * Why every index page has one now
 * ---------------------------------------------------------------------------
 * The pages that sell well — the homepage, an integration, a widget — all show
 * the thing beside the headline. The index pages (pricing, widgets, tools…)
 * were a headline and an empty half-screen, which reads as a page that ran out
 * of things to say before the fold.
 *
 * Each scene is built from the same parts the product is: a review card, a
 * platform mark, a row of gold stars, a layout sketch. So the picture still
 * makes the page's claim — the pricing scene is a free plan and a trial, the
 * manage scene is one review published and one held back — and Jet is the
 * reason it is a scene rather than a diagram.
 *
 * Decoration throughout: each hero says all of this in words beside it.
 */

/** The stage every scene stands on: a soft disc of light and a dashed orbit. */
export function SceneStage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cx(
        'relative mx-auto aspect-square w-full max-w-[22rem] select-none sm:max-w-[26rem] lg:max-w-none',
        className
      )}
    >
      <div className="absolute inset-[4%] rounded-full bg-[radial-gradient(closest-side,rgba(31,91,240,0.13),rgba(109,60,245,0.06)_62%,transparent)]" />
      <div className="rj-orbit absolute inset-[16%] rounded-full border border-dashed border-brand/20" />
      {children}
    </div>
  );
}

/**
 * A Jet that answers back: a press makes it cheer for a moment, then it
 * settles into the mood it was given. Its eyes follow the pointer meanwhile.
 * The one interactive Jet on a page — the rest are scenery.
 */
export function PlayJet({
  mood,
  className,
  style,
}: {
  mood: JetMood;
  className?: string;
  style?: CSSProperties;
}) {
  const [cheering, setCheering] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const cheer = () => {
    clearTimeout(timer.current);
    setCheering(true);
    timer.current = setTimeout(() => setCheering(false), 1600);
  };

  return (
    <div className={cx('jet-enter cursor-pointer', className)} style={style} onPointerDown={cheer}>
      <Jet mood={cheering ? 'cheer' : mood} size="100%" gaze />
    </div>
  );
}

/** Jet in the middle of a stage. */
export function StageJet({
  mood,
  size = '44%',
  offset = 0,
}: {
  mood: JetMood;
  size?: string;
  /** Nudge off centre, in percent of the stage: negative is up. */
  offset?: number;
}) {
  /*
    Centred by the grid and nudged with `top`, never with a translate: the
    entrance animates `translate`, and Tailwind's `-translate-*` utilities set
    the same property, so one would silently cancel the other.
  */
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center">
      <PlayJet mood={mood} className="pointer-events-auto relative" style={{ width: size, top: `${offset}%` }} />
    </div>
  );
}

/**
 * Something floating on the stage. Pops in on its own beat, then bobs.
 *
 * Positioned with percentages so a scene keeps its composition from a phone
 * to a 30rem column; `delay` staggers both the entrance and the bob so the
 * pieces never move in step.
 */
export function Float({
  children,
  className,
  delay = 0,
  style,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cx('rj-chip absolute', className)}
      style={{ ...style, ['--d' as string]: `${delay}s` }}
    >
      {children}
    </div>
  );
}

const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(10,10,11,0.06),0_18px_40px_-18px_rgba(20,40,120,0.35)]';

const PILL = cx(CARD, 'flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px] font-semibold whitespace-nowrap text-ink-800');

function Tick() {
  return (
    <span className="grid size-4 shrink-0 place-items-center rounded-full bg-positive-50 text-positive-700 ring-1 ring-positive-200">
      <svg viewBox="0 0 16 16" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3.5 8.5 3 3 6-7" />
      </svg>
    </span>
  );
}

const AVATAR_TINTS = ['bg-[#ffe3d3] text-[#b5531f]', 'bg-[#dff3e6] text-[#2f7a4a]', 'bg-[#e6e9ff] text-[#3b4cc0]', 'bg-[#fbe3f1] text-[#a63c7d]'];

/** A review, drawn small. */
function MiniReview({
  name,
  source,
  text,
  tint = 0,
  rating = 5,
  className,
  footer,
}: {
  name: string;
  source: ReviewSourceId;
  text: string;
  tint?: number;
  rating?: number;
  className?: string;
  footer?: ReactNode;
}) {
  return (
    <div className={cx(CARD, 'w-[11.5rem] p-3', className)}>
      <div className="flex items-center gap-2">
        <span className={cx('grid size-6 shrink-0 place-items-center rounded-full text-[10px] font-bold', AVATAR_TINTS[tint % AVATAR_TINTS.length])}>
          {name[0]}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold text-ink-900">{name}</span>
        <SourceGlyph source={source} className="size-3.5 shrink-0" />
      </div>
      <Stars rating={rating} size={11} label="" className="mt-2" />
      <p className="mt-1.5 text-[11px] leading-snug text-ink-600">{text}</p>
      {footer}
    </div>
  );
}

/* ==========================================================================
   The scenes, one per page
   ========================================================================== */

export function PricingScene({ trialDays }: { trialDays: number }) {
  return (
    <SceneStage>
      <StageJet mood="hold" />
      <Float className="top-[8%] left-[2%]" delay={0.15}>
        <div className={cx(CARD, 'px-4 py-3')}>
          <p className="text-[11px] font-semibold text-ink-500">Free plan</p>
          <p className="mt-0.5 flex items-baseline gap-1">
            <span className="text-[26px] leading-none font-extrabold tracking-tight text-ink-950">$0</span>
            <span className="text-[11px] text-ink-500">/month, forever</span>
          </p>
        </div>
      </Float>
      <Float className="top-[22%] right-[0%]" delay={0.35}>
        <span className={PILL}>
          <Tick /> {trialDays}-day free trial
        </span>
      </Float>
      <Float className="bottom-[16%] left-[4%]" delay={0.55}>
        <span className={PILL}>
          <span className="text-[15px] leading-none text-brand">∞</span> No view limits
        </span>
      </Float>
      <Float className="right-[6%] bottom-[8%]" delay={0.75}>
        <span className={PILL}>
          <Tick /> Every layout, every plan
        </span>
      </Float>
    </SceneStage>
  );
}

export function WidgetsScene() {
  const sketch = (type: WidgetType) => (
    <div className={cx(CARD, 'w-[8.5rem] p-1.5')}>
      <LayoutSketch type={type} active className="h-[4.6rem]" />
    </div>
  );
  return (
    <SceneStage>
      <StageJet mood="wave" />
      <Float className="top-[6%] left-[4%]" delay={0.15}>{sketch('masonry')}</Float>
      <Float className="top-[16%] right-[0%]" delay={0.35}>{sketch('slider')}</Float>
      <Float className="bottom-[10%] left-[0%]" delay={0.55}>{sketch('grid')}</Float>
      <Float className="right-[4%] bottom-[4%]" delay={0.75}>{sketch('marquee')}</Float>
    </SceneStage>
  );
}

const ORBIT: ReviewSourceId[] = ['google', 'trustpilot', 'yelp', 'tripadvisor', 'airbnb', 'appstore', 'googleplay', 'etsy'];

/** Platform marks circling Jet, who is busy bringing them in. */
export function IntegrationsScene() {
  return (
    <SceneStage>
      <div className="rj-orbit-spin absolute inset-[10%]">
        {ORBIT.map((source, index) => {
          const angle = (index / ORBIT.length) * Math.PI * 2;
          return (
            <span
              key={source}
              className="rj-orbit-counter absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${50 + 50 * Math.cos(angle)}%`, top: `${50 + 50 * Math.sin(angle)}%` }}
            >
              <span className="rj-chip block" style={{ ['--d' as string]: `${0.1 + index * 0.08}s` }}>
                <SourceBadge source={source} className="size-11 shadow-[0_10px_24px_-10px_rgba(20,40,120,0.45)] ring-0 [&_svg]:size-5" />
              </span>
            </span>
          );
        })}
      </div>
      <StageJet mood="work" size="38%" />
      <Float className="right-[18%] bottom-[20%]" delay={0.9}>
        <span className={cx(PILL, 'text-positive-700')}>
          <Tick /> 128 reviews imported
        </span>
      </Float>
    </SceneStage>
  );
}

const SWATCHES = ['#0a0a0b', '#1f5bf0', '#6d3cf5', '#f97316', '#16a34a'];

export function ToolsScene() {
  return (
    <SceneStage>
      <StageJet mood="work" />
      <Float className="top-[8%] left-[2%]" delay={0.15}>
        <div className={cx(CARD, 'flex items-center gap-1.5 px-3 py-2.5')}>
          {SWATCHES.map((colour, index) => (
            <span
              key={colour}
              className={cx('size-5 rounded-full', index === 1 && 'ring-2 ring-brand ring-offset-2')}
              style={{ background: colour }}
            />
          ))}
        </div>
      </Float>
      <Float className="top-[24%] right-[0%]" delay={0.35}>
        <div className={cx(CARD, 'w-[9.5rem] px-3 py-2.5')}>
          <p className="text-[10.5px] font-semibold text-ink-500">Corner radius</p>
          <div className="relative mt-2 h-1.5 rounded-full bg-ink-100">
            <div className="rj-slide absolute inset-y-0 left-0 rounded-full bg-brand" />
          </div>
        </div>
      </Float>
      <Float className="bottom-[6%] left-[6%]" delay={0.55}>
        <MiniReview name="Priya S." source="google" text="Set it up on my lunch break." tint={2} />
      </Float>
    </SceneStage>
  );
}

/** One review going up, one held back: the toggles flip on a loop. */
export function ManageScene() {
  return (
    <SceneStage>
      <StageJet mood="think" size="40%" offset={-4} />
      <Float className="top-[4%] left-[0%]" delay={0.15}>
        <MiniReview
          name="Marco D."
          source="tripadvisor"
          text="Best pasta we had all trip."
          tint={0}
          footer={<Toggle label="Published" on />}
        />
      </Float>
      <Float className="right-[0%] bottom-[4%]" delay={0.4}>
        <MiniReview
          name="Jess K."
          source="yelp"
          rating={2}
          text="Waited a while for a table."
          tint={3}
          footer={<Toggle label="Not shown" />}
        />
      </Float>
    </SceneStage>
  );
}

function Toggle({ label, on = false }: { label: string; on?: boolean }) {
  return (
    <div className="mt-2.5 flex items-center justify-between border-t border-ink-100 pt-2">
      <span className={cx('text-[10.5px] font-semibold', on ? 'text-positive-700' : 'text-ink-500')}>{label}</span>
      <span className={cx('relative h-4 w-7 rounded-full transition-colors', on ? 'bg-positive-700' : 'bg-ink-200')}>
        <span className={cx('absolute top-0.5 size-3 rounded-full bg-white shadow-sm', on ? 'left-3.5' : 'left-0.5')} />
      </span>
    </div>
  );
}

/** A form being answered: the stars fill one by one, and Jet cheers. */
export function FormsScene() {
  return (
    <SceneStage>
      <StageJet mood="cheer" size="38%" offset={12} />
      <Float className="inset-x-0 top-[6%] mx-auto w-fit" delay={0.15}>
        <div className={cx(CARD, 'w-[14rem] px-4 py-3.5 text-center')}>
          <p className="text-[12.5px] font-bold text-ink-950">How was your visit?</p>
          <div className="mt-2.5 flex justify-center gap-1.5">
            {[0, 1, 2, 3, 4].map((index) => (
              <svg key={index} viewBox="0 0 20 20" className="rj-fill-star size-6" style={{ ['--i' as string]: index }}>
                <path d="M10 1.4l2.5 5.07 5.6.82-4.05 3.95.96 5.57L10 14.19l-5.01 2.62.96-5.57L1.9 7.29l5.6-.82z" />
              </svg>
            ))}
          </div>
          <div className="mt-3 h-7 rounded-lg bg-ink-50" />
        </div>
      </Float>
      <Float className="bottom-[12%] left-[0%]" delay={0.5}>
        <span className={PILL}>
          <SourceGlyph source="google" className="size-3.5" /> Sent on to Google
        </span>
      </Float>
      <Float className="right-[0%] bottom-[24%]" delay={0.7}>
        <span className={PILL}>
          <Tick /> Thanks, Ana!
        </span>
      </Float>
    </SceneStage>
  );
}

function MiniBrowser({ address, className }: { address: string; className?: string }) {
  return (
    <div className={cx(CARD, 'w-[12rem] overflow-hidden', className)}>
      <div className="flex items-center gap-1 bg-ink-50 px-2.5 py-1.5">
        <span className="size-1.5 rounded-full bg-[#ff5f57]" />
        <span className="size-1.5 rounded-full bg-[#febc2e]" />
        <span className="size-1.5 rounded-full bg-[#28c840]" />
        <span className="ml-1.5 truncate text-[9.5px] text-ink-500">{address}</span>
      </div>
      <div className="flex gap-1.5 p-2">
        {[0, 1, 2].map((index) => (
          <div key={index} className="flex-1 rounded-md bg-ink-50 p-1.5">
            <Stars rating={5} size={6} label="" />
            <div className="mt-1 h-1 rounded bg-ink-200" />
            <div className="mt-0.5 h-1 w-2/3 rounded bg-ink-150" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AgenciesScene() {
  return (
    <SceneStage>
      <StageJet mood="wave" size="40%" offset={4} />
      <Float className="top-[4%] left-[0%]" delay={0.15}>
        <MiniBrowser address="bloomflorist.com" />
      </Float>
      <Float className="top-[18%] right-[0%]" delay={0.35}>
        <MiniBrowser address="northgatecycles.co.uk" />
      </Float>
      <Float className="bottom-[4%] left-[8%]" delay={0.55}>
        <MiniBrowser address="casaluna.mx" />
      </Float>
    </SceneStage>
  );
}

export function CompareScene() {
  return (
    <SceneStage>
      <StageJet mood="wow" />
      <Float className="top-[10%] left-[0%]" delay={0.15}>
        <span className={PILL}>
          <Tick /> Every fact links to its source
        </span>
      </Float>
      <Float className="top-[30%] right-[0%]" delay={0.35}>
        <span className={PILL}>
          <Tick /> Date checked, shown
        </span>
      </Float>
      <Float className="bottom-[10%] left-[6%]" delay={0.55}>
        <span className={PILL}>
          <Tick /> Says when they&rsquo;re the better pick
        </span>
      </Float>
    </SceneStage>
  );
}

const TRADES: { label: string; source: ReviewSourceId }[] = [
  { label: 'Restaurants', source: 'tripadvisor' },
  { label: 'Holiday lets', source: 'airbnb' },
  { label: 'Clinics', source: 'google' },
  { label: 'Online shops', source: 'trustpilot' },
  { label: 'Apps', source: 'appstore' },
  { label: 'Makers', source: 'etsy' },
];

const TRADE_SPOTS = [
  'top-[6%] left-[4%]',
  'top-[12%] right-[2%]',
  'top-[44%] left-[-2%]',
  'top-[48%] right-[-2%]',
  'bottom-[8%] left-[8%]',
  'bottom-[4%] right-[10%]',
];

export function WhoScene() {
  return (
    <SceneStage>
      <StageJet mood="wave" size="40%" />
      {TRADES.map((trade, index) => (
        <Float key={trade.label} className={TRADE_SPOTS[index]} delay={0.12 + index * 0.12}>
          <span className={PILL}>
            <SourceGlyph source={trade.source} className="size-3.5" /> {trade.label}
          </span>
        </Float>
      ))}
    </SceneStage>
  );
}

export function BlogScene() {
  const article = (title: string, minutes: number) => (
    <div className={cx(CARD, 'w-[12rem] p-2.5')}>
      <div className="h-14 rounded-lg bg-[linear-gradient(135deg,#e6edff_0%,#efe9ff_100%)]" />
      <p className="mt-2 text-[11.5px] leading-snug font-semibold text-ink-900">{title}</p>
      <p className="mt-1 text-[10px] text-ink-500">{minutes} min read</p>
    </div>
  );
  return (
    <SceneStage>
      <StageJet mood="think" size="40%" />
      <Float className="top-[2%] left-[2%]" delay={0.15}>{article('How to ask for a review', 6)}</Float>
      <Float className="right-[0%] bottom-[6%]" delay={0.4}>{article('Which reviews to publish', 4)}</Float>
    </SceneStage>
  );
}

/* ==========================================================================
   Scenes for the pages made from data — drawn from the page's own content
   ========================================================================== */

const SCATTER = [
  'top-[6%] left-[4%]',
  'top-[14%] right-[2%]',
  'bottom-[18%] left-[0%]',
  'bottom-[6%] right-[8%]',
];

/** An industry: where its reviews live, and one of them on the way over. */
export function IndustryScene({ sources }: { sources: ReviewSourceId[] }) {
  const first = sources[0] ?? 'google';
  return (
    <SceneStage>
      <StageJet mood="wave" size="40%" offset={-4} />
      {sources.slice(0, 2).map((source, index) => (
        <Float key={source} className={SCATTER[index]} delay={0.15 + index * 0.15}>
          <SourceBadge source={source} className="size-12 shadow-[0_10px_24px_-10px_rgba(20,40,120,0.45)] ring-0 [&_svg]:size-6" />
        </Float>
      ))}
      <Float className="right-[0%] bottom-[4%]" delay={0.5}>
        <MiniReview name="Sam R." source={first} text="Would recommend to anyone." tint={1} />
      </Float>
      <Float className="bottom-[22%] left-[0%]" delay={0.7}>
        <span className={PILL}>
          <SourceGlyph source={first} className="size-3.5" /> {REVIEW_SOURCE_DEFINITIONS[first].label} reviews, on your site
        </span>
      </Float>
    </SceneStage>
  );
}

/** A website builder: its site in a window, the reviews on it, Jet beside. */
export function PlatformScene({ name, claim }: { name: string; claim: string }) {
  return (
    <SceneStage>
      <StageJet mood="hold" size="38%" offset={10} />
      <Float className="inset-x-0 top-[4%] mx-auto w-fit" delay={0.15}>
        <MiniBrowser address={`your${name.toLowerCase().replace(/\W+/g, '')}site.com`} className="w-[15rem]" />
      </Float>
      <Float className="bottom-[16%] left-[0%]" delay={0.45}>
        <span className={PILL}>
          <Tick /> {claim}
        </span>
      </Float>
      <Float className="right-[0%] bottom-[6%]" delay={0.65}>
        <span className={PILL}>
          <span className="text-brand">&lt;/&gt;</span> One line of code
        </span>
      </Float>
    </SceneStage>
  );
}

/** A source on a builder: the platform mark, carried by Jet to the site. */
export function CombinationScene({ source, platform }: { source: ReviewSourceId; platform: string }) {
  return (
    <SceneStage>
      <StageJet mood="work" size="36%" />
      <Float className="top-[10%] left-[4%]" delay={0.15}>
        <SourceBadge source={source} className="size-14 shadow-[0_10px_24px_-10px_rgba(20,40,120,0.45)] ring-0 [&_svg]:size-7" />
      </Float>
      <Float className="right-[0%] bottom-[8%]" delay={0.45}>
        <MiniBrowser address={`your${platform.toLowerCase().replace(/\W+/g, '')}site.com`} />
      </Float>
      <svg viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 size-full" fill="none" aria-hidden>
        <path
          className="rj-flow-dash"
          d="M22 26 C40 30 36 48 50 50 S66 66 74 74"
          stroke="var(--color-brand)"
          strokeOpacity="0.45"
          strokeWidth="0.8"
          strokeLinecap="round"
          pathLength={240}
        />
      </svg>
    </SceneStage>
  );
}

/** ReviewsJet against a competitor: two cards, a "vs", Jet weighing it up. */
export function VersusScene({ name }: { name: string }) {
  const card = (label: string, mark: ReactNode, className: string, delay: number) => (
    <Float className={className} delay={delay}>
      <div className={cx(CARD, 'flex w-[9.5rem] flex-col items-center gap-2 px-3 py-4')}>
        {mark}
        <span className="text-[12px] font-bold text-ink-900">{label}</span>
        <div className="h-1.5 w-16 rounded-full bg-ink-100" />
        <div className="h-1.5 w-10 rounded-full bg-ink-100" />
      </div>
    </Float>
  );
  return (
    <SceneStage>
      <StageJet mood="think" size="34%" offset={18} />
      {card('ReviewsJet', <Jet mood="idle" size={36} still />, 'top-[6%] left-[2%]', 0.15)}
      {card(
        name,
        <span className="grid size-9 place-items-center rounded-xl bg-ink-100 text-[14px] font-extrabold text-ink-600">
          {name[0]}
        </span>,
        'top-[6%] right-[2%]',
        0.35
      )}
      <Float className="inset-x-0 top-[17%] mx-auto w-fit" delay={0.55}>
        <span className="grid size-10 place-items-center rounded-full bg-ink-950 text-[12px] font-extrabold text-white shadow-lg">
          vs
        </span>
      </Float>
    </SceneStage>
  );
}
